import * as React from "react"
import { useState } from "react"
import { useFeatureFlagEnabled } from "posthog-js/react"

import { NavUser } from "@/components/nav-user"
import { NavMain } from "@/components/nav-main"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar"
import { Avatar, AvatarImage } from "@/components/ui/avatar"
import { useAuth } from "@/context/auth-context"
import { useTheme } from "@/context/theme-context"
import { useApi } from "@/hooks/useApi"
import { InsightsIcon, MessageIcon, CompassIcon, SettingsGearIcon, HistoryIcon, RefreshCw, NotificationsIcon, FeedbackIcon } from "@/components/icons"
import { useNotifications } from "@/context/notifications-context"
import { NotificationsDialog } from "@/components/notifications-dialog"
import { SuggestionDialog } from "@/components/suggestion-dialog"
import { toast } from "sonner"
import { FeatureFlags } from "@/lib/feature-flags"

interface ManualRefreshResponse {
  success: boolean
  message?: string
  data?: {
    calendarsFound: number
    calendarsTriggered: number
    calendarsReactivated: number
    syncBatchId: string
  }
  messageUser?: string
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth()
  const { theme } = useTheme()
  const { isMobile, setOpenMobile } = useSidebar()
  const { post, loading: apiLoading } = useApi()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [suggestionOpen, setSuggestionOpen] = useState(false)
  const { unreadCount } = useNotifications()
  
  // Feature flag for dev/beta features (Koba only; Planning is always available)
  const hasDevFeaturesAccess = useFeatureFlagEnabled(FeatureFlags.DEV_FEATURES_ACCESS) === true

  // Navigation data - Planning is always visible; Koba is gated behind dev features flag
  const platformItems = [
    {
      title: "Insights",
      url: "/insights",
      icon: InsightsIcon,
      isActive: false,
      items: [],
    },
    ...(hasDevFeaturesAccess ? [{
      title: "Koba",
      url: "/koba",
      icon: MessageIcon,
      isActive: false,
      items: [],
    }] : []),
    {
      title: "Planning",
      url: "/planning",
      icon: CompassIcon,
      isActive: false,
      items: [],
    },
  ]

  // Handle manual refresh of calendars
  const handleRefreshCalendars = async () => {
    if (isRefreshing || apiLoading) return

    setIsRefreshing(true)
    
    // Close sidebar on mobile
    if (isMobile) {
      setOpenMobile(false)
    }

    try {
      const response = await post<ManualRefreshResponse>(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-manual-refresh`
      )

      if (response.success && response.data?.success) {
        const { calendarsTriggered = 0, calendarsReactivated = 0 } = response.data.data || {}
        
        if (calendarsTriggered === 0 && calendarsReactivated === 0) {
          toast.info('No calendars to refresh')
        } else {
          toast.success(response.data.message || 'Calendars refreshing...')
        }
      } else {
        toast.error(response.data?.messageUser || response.error || 'Failed to refresh calendars')
      }
    } catch (err) {
      console.error('Failed to refresh calendars:', err)
      toast.error('Failed to refresh calendars')
    } finally {
      setIsRefreshing(false)
    }
  }

  // Get the current effective theme (including system theme)
  const getCurrentTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return theme
  }

  // Get the appropriate logo based on theme
  const getLogoSrc = () => {
    const currentTheme = getCurrentTheme()
    return currentTheme === 'dark' 
      ? '/Calintent-transparent-dark-teal-400.png'
      : '/Calintent-transparent-light-teal-500.png'
  }

  const settingsItems = [
    {
      title: "Settings",
      url: "/settings",
      icon: SettingsGearIcon,
      isActive: false,
      items: [],
    },
    {
      title: "Executions",
      url: "/automation-executions",
      icon: HistoryIcon,
      isActive: false,
      items: [],
    },
  ]

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center w-full py-1 h-12">
              <Avatar className="h-8 w-8 rounded-none">
                <AvatarImage 
                  src={getLogoSrc()} 
                  alt="Calintent" 
                  className="h-8 w-8 object-cover"
                />
              </Avatar>
              <div className="flex flex-1 items-baseline text-left ml-2 group-data-[collapsible=icon]:hidden">
                <span className="truncate font-jakarta font-semibold text-2xl text-foreground">Calintent</span>
              </div>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={platformItems} showSectionTitle={false} />
        <NavMain items={settingsItems} sectionTitle="Settings" />
      </SidebarContent>
      
      {/* Actions Section - Refresh & Notifications */}
      <div className="mt-auto px-2 py-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton 
              tooltip="Refresh Calendars"
              onClick={handleRefreshCalendars}
              disabled={isRefreshing || apiLoading}
            >
              <RefreshCw className={isRefreshing ? 'animate-spin' : ''} strokeWidth="1" />
              <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              tooltip="Notifications"
              onClick={() => setNotificationsOpen(true)}
            >
              <div className="relative">
                <NotificationsIcon />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-destructive" />
                )}
              </div>
              <span>Notifications</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              tooltip="Suggestion"
              onClick={() => setSuggestionOpen(true)}
            >
              <FeedbackIcon />
              <span>Suggestion</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </div>
      
      <SidebarFooter className="border-t border-sidebar-border pb-8">
        {user && (
          <NavUser 
            user={{
              name: user.user_metadata?.full_name || user.email || 'User',
              email: user.email || '',
              avatar: user.user_metadata?.avatar_url || ''
            }} 
          />
        )}
      </SidebarFooter>
      <SidebarRail />
      
      {/* Notifications Dialog */}
      <NotificationsDialog open={notificationsOpen} onOpenChange={setNotificationsOpen} />
      
      {/* Suggestion Dialog */}
      <SuggestionDialog open={suggestionOpen} onOpenChange={setSuggestionOpen} />
    </Sidebar>
  )
}
